import { Box, HStack, Flex, Text, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Table,
} from '@tanstack/react-table';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';
import {
  ValleyBankLoanAccountWithCollateralStats,
  parse,
} from '@finley-technologies/valley-bank-utils';

import {
  useGetValleyBankLatestPinnedCommentForLoanAccountsLazyQueryGQLType,
  useGetValleyBankLoanAccountCommentsQuery,
  useGetValleyBankUnderMarginLoanAccountsQuery,
  ValleyBankLoanAccountLatestPinnedCommentGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  LoanAccountsUnderMarginOverviewUpdatesDrawer,
  LoanAccountUnderMarginDetailedUpdatesDrawer,
} from './LoanAccountsCommentsDrawers';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import {
  getUnexpectedErrorMessage,
  formatCurrencyAmount,
} from 'packages/common-frontend/utils';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import {
  Alert,
  ProgressAlert,
} from '~/packages/common-frontend/components/Alert';
import { CsvXlsxDownloadSelect } from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from '~/packages/common-frontend/components/icons';
import { RenderStaticHtml } from '~/packages/common-frontend/components/RichTextEditor/RichTextEditor';
import { TanStackPaginationControl } from '~/packages/common-frontend/components/TanStack';
import { ColumnDisplaySettingsButton } from '~/packages/common-frontend/components/TanStack/ColumnReordering';
import {
  errorToastConfig,
  errorToastId,
} from '~/packages/common-frontend/constants';
import {
  getColumnNames,
  createDefaultTableColumnConfiguration,
  TableColumnConfigurationKey,
  useTableColumnPreferences,
} from '~/packages/common-frontend/hooks/useTableColumnPreferences';
import { InfoCard } from '~/valleyBank/domains/components/InfoCard';
import { LoanAccountsOverviewTemplate } from '~/valleyBank/domains/LoanAccounts/ui/Overview/LoanAccountsOverviewTemplate';
import {
  LoanAccountsTableContainer,
  loanAccountsColumnDefs,
} from '~/valleyBank/domains/LoanAccounts/ui/Overview/LoanAccountsTable';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';
import { DashboardMode } from '~/valleyBank/domains/page/hooks/DataMetadataContextType';

type SelectedLoanAccount = {
  loanAccountNumber: string;
  cifNum: string;
  name: string;
};

const TABLE_KEY_NON_PREVIEW: TableColumnConfigurationKey =
  'valleyBankSbl.loanAccountsUnderMarginNonPreview';

const TABLE_KEY_PREVIEW: TableColumnConfigurationKey =
  'valleyBankSbl.loanAccountsUnderMarginPreview';

const DEFAULT_TABLE_CONFIG_NON_PREVIEW = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY_NON_PREVIEW,
  columns: [
    { columnKey: 'loanAccountNumber', isVisible: true, isFixed: false },
    { columnKey: 'pinnedComment', isVisible: true, isFixed: false },
    { columnKey: 'borrower', isVisible: true, isFixed: false },
    { columnKey: 'outstandingBalance', isVisible: true, isFixed: false },
    { columnKey: 'assignedMarketValue', isVisible: true, isFixed: false },
    { columnKey: 'effectiveAdvanceRate', isVisible: true, isFixed: false },
    {
      columnKey: 'assignedPreAllocationLendableValue',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'effectiveAllocationRate',
      isVisible: false,
      isFixed: false,
    },
    {
      columnKey: 'assignedPostAllocationLendableValue',
      isVisible: false,
      isFixed: false,
    },
    { columnKey: 'marginAvailability', isVisible: true, isFixed: false },
    { columnKey: 'marginAvailabilityRate', isVisible: false, isFixed: false },
    { columnKey: 'arrangementGroupId', isVisible: true, isFixed: false },
    { columnKey: 'cifNum', isVisible: true, isFixed: false },
    { columnKey: 'numberOfLoanParties', isVisible: true, isFixed: false },
  ],
});

const DEFAULT_TABLE_CONFIG_PREVIEW = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY_PREVIEW,
  columns: [
    { columnKey: 'loanAccountNumber', isVisible: true, isFixed: false },
    { columnKey: 'borrower', isVisible: true, isFixed: false },
    { columnKey: 'outstandingBalance', isVisible: true, isFixed: false },
    { columnKey: 'assignedMarketValue', isVisible: true, isFixed: false },
    { columnKey: 'effectiveAdvanceRate', isVisible: true, isFixed: false },
    {
      columnKey: 'assignedPreAllocationLendableValue',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'effectiveAllocationRate',
      isVisible: false,
      isFixed: false,
    },
    {
      columnKey: 'assignedPostAllocationLendableValue',
      isVisible: false,
      isFixed: false,
    },
    { columnKey: 'marginAvailability', isVisible: true, isFixed: false },
    { columnKey: 'marginAvailabilityRate', isVisible: false, isFixed: false },
    { columnKey: 'arrangementGroupId', isVisible: true, isFixed: false },
    { columnKey: 'cifNum', isVisible: true, isFixed: false },
    { columnKey: 'numberOfLoanParties', isVisible: true, isFixed: false },
  ],
});

type GeneratePinnedCommentColumnDefInput = {
  pinnedCommentMap: any;
  loanAccountToThreadsCountMap: any;
  selectedLoanAccount: SelectedLoanAccount | undefined;
  setSelectedLoanAccount: React.Dispatch<
    React.SetStateAction<SelectedLoanAccount | undefined>
  >;
};

type AllUndefined<T> = {
  [K in keyof T]: undefined;
};

const generatePinnedCommentColumnDef = ({
  pinnedCommentMap,
  loanAccountToThreadsCountMap,
  selectedLoanAccount,
  setSelectedLoanAccount,
}: GeneratePinnedCommentColumnDefInput): ColumnDef<
  ValleyBankLoanAccountWithCollateralStats,
  any
> => ({
  id: 'pinnedComment',
  header: 'Comment',
  enableSorting: false,
  enableColumnFilter: false,
  // eslint-disable-next-line react/display-name
  cell: ({ row }) => {
    const { loanAccountNumber, cifNum, accountName } = row.original;
    const pinnedComment =
      pinnedCommentMap && pinnedCommentMap[loanAccountNumber]
        ? pinnedCommentMap[loanAccountNumber].pinnedComment.comment
        : undefined;
    return (
      <Flex gap="1em" alignItems="center" p="1em 0">
        <IconButtonV2
          aria-label="Updates"
          icon={<Icon icon="bell" />}
          variant="v2-ghost"
          tooltipEnabled={false}
          isActive={
            loanAccountNumber === selectedLoanAccount?.loanAccountNumber
          }
          hasDotIndicator={
            loanAccountToThreadsCountMap &&
            loanAccountToThreadsCountMap[loanAccountNumber]
              ? loanAccountToThreadsCountMap[loanAccountNumber] > 0
              : false
          }
          onClick={() => {
            setSelectedLoanAccount({
              loanAccountNumber,
              cifNum,
              name: accountName ?? '',
            });
          }}
        />
        <Box flexGrow={1}>
          {pinnedComment && pinnedComment.length > 0 ? (
            <RenderStaticHtml textHtml={pinnedComment} color="gray.600" />
          ) : (
            <Text>-</Text>
          )}
        </Box>
      </Flex>
    );
  },
  meta: {
    style: {
      padding: '0px',
      textAlign: 'left',
      minWidth: '12em',
    },
  },
});

const generateColumnDefs = (
  input:
    | (AllUndefined<GeneratePinnedCommentColumnDefInput> & {
        dashboardMode: Extract<DashboardMode, 'preview'>;
      })
    | (GeneratePinnedCommentColumnDefInput & {
        dashboardMode: Exclude<DashboardMode, 'preview'>;
      }),
): ColumnDef<ValleyBankLoanAccountWithCollateralStats, any>[] => {
  if (input.dashboardMode === 'preview') {
    return loanAccountsColumnDefs;
  }
  return [
    loanAccountsColumnDefs[0],
    generatePinnedCommentColumnDef(input),
    ...loanAccountsColumnDefs.slice(1),
  ];
};

export const getUnderMarginReportDataForDownload = ({
  tableInstance,
  pinnedCommentMap,
  columnIdsToExclude = [],
}: {
  tableInstance: Table<any>;
  pinnedCommentMap?: {
    [key: string]: ValleyBankLoanAccountLatestPinnedCommentGQLType;
  };
  columnIdsToExclude?: string[];
}) => {
  const dataRows = _.map(tableInstance.getFilteredRowModel().rows, (row) => {
    const dataRow: { [key: string]: any } = {};

    // logic to get pinned comment if it exists
    const cellsByColumnId = row._getAllCellsByColumnId();
    const loanAccountNumber = String(
      cellsByColumnId.loanAccountNumber?.getValue() ?? '',
    );
    const pinnedComment = pinnedCommentMap
      ? pinnedCommentMap[loanAccountNumber]?.pinnedComment.comment
      : '-';

    _.forEach(row.getVisibleCells(), (cell) => {
      const key = cell.column.columnDef.header?.toString() ?? cell.column.id;
      if (cell.column.id === 'pinnedComment') {
        dataRow[key] = pinnedComment;
      } else if (
        cell.column.id === undefined ||
        !columnIdsToExclude.includes(cell.column.id)
      ) {
        // only add data if column has no id OR column id is NOT in `columnIdsToExclude`
        dataRow[key] = cell.getValue();
      }
    });
    return dataRow;
  });

  const headers = _.map(_.keys(dataRows[0]), (headerKey) => {
    return {
      label: commonUtils.toTitleCase(headerKey),
      key: headerKey,
    };
  });

  return { dataRows, headers };
};

const RouteBase = () => {
  const toast = useToast();
  const navigate = useNavigate();

  const { reportingDate, dashboardMode, isPreview } = useDataMetadataContext();
  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences(
    dashboardMode === 'preview'
      ? {
          tableKey: TABLE_KEY_PREVIEW,
          defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG_PREVIEW,
        }
      : {
          tableKey: TABLE_KEY_NON_PREVIEW,
          defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG_NON_PREVIEW,
        },
  );

  const [isLoading, setIsLoading] = useState(true);
  const [loanAccounts, setLoanAccounts] = useState<
    ValleyBankLoanAccountWithCollateralStats[]
  >([]);
  // selected loan account is to open a drawer with comments specific to that loan account
  const [
    selectedLoanAccount,
    setSelectedLoanAccount,
  ] = useState<SelectedLoanAccount>();

  let routeLevelError = false;

  const { error } = useGetValleyBankUnderMarginLoanAccountsQuery({
    variables: {
      reportingDate,
      isPreview,
    },
    onCompleted: (data) => {
      if (data.valleyBankUnderMarginLoanAccounts) {
        try {
          const newLoanAccounts = parse.parseValleyBankLoanAccountWithCollateralStatsArray(
            data.valleyBankUnderMarginLoanAccounts,
          );
          const sortedLoanAccounts = newLoanAccounts.sort(
            (a, b) => a.marginAvailable - b.marginAvailable,
          );
          setLoanAccounts(sortedLoanAccounts);
        } catch (err) {
          Sentry.captureException(err);
        } finally {
          setIsLoading(false);
        }
      }
    },
    onError: () => {
      if (!routeLevelError && !toast.isActive(errorToastId)) {
        errorToastConfig.title = `Failed to load loan accounts under margin report page`;
        routeLevelError = true;
        toast(errorToastConfig);
        navigate('/');
      }
    },
  });

  // get all comments ONLY for current dashboard mode
  const {
    data: threadsData,
    refetch: refetchThreads,
  } = useGetValleyBankLoanAccountCommentsQuery({
    variables: {},
    skip: dashboardMode !== 'current',
    fetchPolicy: 'network-only',
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });

  const loanAccountToThreadsCountMap = useMemo(() => {
    if (threadsData) {
      const result: Record<string, number> = {};
      threadsData.valleyBankLoanAccountComments.forEach(
        ({ loanAccountNum }) => {
          const count = result[loanAccountNum] ?? 0;
          result[loanAccountNum] = count + 1;
        },
      );

      return result;
    }

    return undefined;
  }, [threadsData]);

  // get pinned comments
  const [
    valleyBankLatestPinnedCommentForLoanAccounts,
    { data: latestPinnedCommentData, refetch: refetchLatestPinnedComment },
  ] = useGetValleyBankLatestPinnedCommentForLoanAccountsLazyQueryGQLType({
    fetchPolicy: 'network-only',
    onError: () => {
      if (!routeLevelError && !toast.isActive(errorToastId)) {
        errorToastConfig.title = `Failed to load loan accounts under margin report page`;
        routeLevelError = true;
        toast(errorToastConfig);
        navigate('/');
      }
    },
  });

  useEffect(() => {
    if (loanAccounts.length > 0 && !isLoading && dashboardMode !== 'preview') {
      valleyBankLatestPinnedCommentForLoanAccounts({
        variables: {
          loanAccountNums: loanAccounts.map(
            ({ loanAccountNumber }) => loanAccountNumber,
          ),
          reportingDate,
          isHistorical: dashboardMode === 'historical',
        },
      });
    }
  }, [
    isLoading,
    loanAccounts,
    reportingDate,
    dashboardMode,
    valleyBankLatestPinnedCommentForLoanAccounts,
  ]);

  const pinnedCommentMap = useMemo(() => {
    if (latestPinnedCommentData) {
      return _.keyBy(
        latestPinnedCommentData.valleyBankLatestPinnedCommentForLoanAccounts,
        (m) => m.loanAccountNum,
      );
    }
    return undefined;
  }, [latestPinnedCommentData]);

  const { columnDefs, columnNames } = useMemo(() => {
    if (dashboardMode === 'preview') {
      const _columnDefs = generateColumnDefs({
        dashboardMode: 'preview',
        pinnedCommentMap: undefined,
        loanAccountToThreadsCountMap: undefined,
        selectedLoanAccount: undefined,
        setSelectedLoanAccount: undefined,
      });

      return {
        columnDefs: _columnDefs,
        columnNames: getColumnNames(_columnDefs),
      };
    }
    const _columnDefs = generateColumnDefs({
      dashboardMode,
      pinnedCommentMap,
      loanAccountToThreadsCountMap,
      selectedLoanAccount,
      setSelectedLoanAccount,
    });
    return {
      columnDefs: _columnDefs,
      columnNames: getColumnNames(_columnDefs),
    };
  }, [
    dashboardMode,
    pinnedCommentMap,
    loanAccountToThreadsCountMap,
    selectedLoanAccount,
    setSelectedLoanAccount,
  ]);

  const table = useReactTable({
    data: loanAccounts,
    columns: columnDefs,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
    },
    state: {
      columnOrder,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const tableDataForDownload = useMemo(
    () =>
      getUnderMarginReportDataForDownload({
        tableInstance: table,
        pinnedCommentMap,
      }),
    [table, pinnedCommentMap],
  );

  const totalUnderMargin = loanAccounts.reduce(
    (acc, { marginAvailable }) => acc + marginAvailable,
    0,
  );

  const totalLoanAccountsUnderMargin = loanAccounts.length;

  const averageUnderMargin = commonUtils.divideByOrZero(
    totalUnderMargin,
    totalLoanAccountsUnderMargin,
  );

  return (
    <>
      <LoanAccountsOverviewTemplate
        selectedTab="underMarginReport"
        headerChildren={
          <LoanAccountsUnderMarginOverviewUpdatesDrawer
            threads={threadsData?.valleyBankLoanAccountComments}
            refetchThreads={() => {
              if (refetchLatestPinnedComment) {
                refetchLatestPinnedComment();
              }
              refetchThreads();
            }}
          />
        }
      >
        {isLoading && (
          <ProgressAlert>Loading under margin report</ProgressAlert>
        )}
        {!isLoading && loanAccounts.length === 0 && (
          <Alert status="info">
            <Text>No loan accounts under margin found</Text>
          </Alert>
        )}
        {!isLoading && loanAccounts.length > 0 && (
          <>
            <HStack w="100%" spacing="24px" p="40px 0">
              <InfoCard
                title="Total Under Margin"
                value={formatCurrencyAmount(
                  totalUnderMargin,
                  Bank.Currency.USD,
                )}
                icon={<Icon icon="dollar" />}
                flexGrow={1}
              />
              <InfoCard
                title="Number of Loan Accounts Under Margin"
                value={totalLoanAccountsUnderMargin.toString()}
                icon={<Icon icon="hashtag" />}
                flexGrow={1}
              />
              <InfoCard
                title="Average Under Margin"
                value={formatCurrencyAmount(
                  averageUnderMargin,
                  Bank.Currency.USD,
                )}
                icon={<Icon icon="dollar" />}
                flexGrow={1}
              />
            </HStack>
            <HStack w="100%" justify="flex-end">
              <ColumnDisplaySettingsButton
                initialColumns={columnConfiguration}
                updateColumnConfigs={updateUserColumnConfiguration}
                columnNames={columnNames}
              />
              <CsvXlsxDownloadSelect
                dataRows={tableDataForDownload.dataRows}
                headers={tableDataForDownload.headers}
                filenamePrefix="loan-accounts-under-margin-report"
              />
            </HStack>
            <LoanAccountsTableContainer
              table={table}
              isLoading={isLoading}
              isError={Boolean(error)}
            />
            <TanStackPaginationControl table={table} />
          </>
        )}
      </LoanAccountsOverviewTemplate>

      {/* ONLY show detail updates drawer for current dashboard  */}
      {selectedLoanAccount && dashboardMode === 'current' && (
        <LoanAccountUnderMarginDetailedUpdatesDrawer
          loanAccountInfo={selectedLoanAccount}
          isOpen={!!selectedLoanAccount}
          onClose={() => {
            setSelectedLoanAccount(undefined);
            if (refetchLatestPinnedComment) {
              refetchLatestPinnedComment();
            }
            refetchThreads();
          }}
        />
      )}
    </>
  );
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <Navigate to="../" />;
    }}
  >
    <RouteBase />
  </RouteErrorBoundary>
);
